import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FiExternalLink } from "react-icons/fi";
import { BiLinkAlt } from "react-icons/bi";
import { FaTiktok, FaInstagram, FaYoutube, FaExternalLinkAlt } from "react-icons/fa";
import "./CreatorDashboard.css";

// Platform icon mapping (Simple Icons minimalist style)
const platformIcons = {
  tiktok:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tiktok.svg",
  instagram:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instagram.svg",
  youtube:
    "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/youtube.svg",
};


// Post card component based on ActivePartnerships
const PostCard = ({
  avatar_url,
  name,
  username,
  followers,
  status,
  lastUpdate,
  platform,
  profile_url,
  posts,
  expanded,
  onClick,
  onProvideFeedback,
  onCompleteFeedback,
}) => {
  const normalized = (status || "").toLowerCase().replace(/_/g, " ").trim();

  const getStatusBadge = () => {
    switch (status) {
      case "under_review":
        return "under review";
      default:
        return normalized;
    }
  };

  return (
    <>
      <article
        className="post-card"
        onClick={(e) => {
          if (e.target.closest(".profile-link-icon")) return;
          onClick();
        }}
        style={{ cursor: "pointer" }}
      >
        <div className="post-profile">
          <div style={{ position: "relative", display: "inline-block" }}>
            <img src={avatar_url} alt={name} className="post-avatar" />
            {platform && platformIcons[platform] && (
              <img
                src={platformIcons[platform]}
                alt={platform}
                style={{
                  width: 24,
                  height: 24,
                  position: "absolute",
                  right: -6,
                  bottom: -6,
                  background: "#fff",
                  padding: 2,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}
              />
            )}
          </div>
          <div className="post-info" style={{ position: "relative" }}>
            <h3
              className="post-name"
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              {name}
              <a
                href={profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="profile-link-icon"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  marginLeft: 6,
                }}
                title="Go to profile"
              >
                <FiExternalLink
                  size={20}
                  color="#888"
                  style={{ opacity: 0.7 }}
                />
              </a>
            </h3>
            <p className="post-meta">
              {username} <span className="separator">|</span> {followers}{" "}
              followers
            </p>
          </div>
        </div>
        <div className="post-status">
          <span className={`post-badge ${status}`}>{getStatusBadge()}</span>
          <p className="post-update">{lastUpdate}</p>
        </div>
      </article>

      {/* Display all posts when expanded, following current influencer */}
      {expanded &&
        posts &&
        posts.length > 0 &&
        posts.map((post, idx) => {
          const hashtags = post.title
            ? post.title.match(/#[\w\d_\u4e00-\u9fa5]+/g)
            : [];
          return (
            <div
              key={idx}
              style={{
                margin: "0 0 18px 0",
                background: "#f8fafd",
                borderRadius: 12,
                padding: 18,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "flex-start",
                gap: 24,
                width: "100%",
                marginLeft: 0,
                flexWrap: "wrap",
              }}
            >
              <a
                href={post.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "inline-block", marginTop: 4 }}
              >
                <BiLinkAlt
                  size={28}
                  color="#888"
                  style={{ opacity: 0.7, verticalAlign: "middle" }}
                />
              </a>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 18,
                    wordBreak: "break-all",
                  }}
                >
                  {post.title &&
                    post.title.replace(/#[\w\d_\u4e00-\u9fa5]+/g, "").trim()}
                </div>
                {hashtags && hashtags.length > 0 && (
                  <div
                    style={{
                      margin: "6px 0 0 0",
                      fontSize: 15,
                      color: "#1976d2",
                      fontWeight: 600,
                      wordBreak: "break-all",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {hashtags.map((tag, i) => (
                      <span
                        key={i}
                        style={{
                          background: "#eaf2ff",
                          borderRadius: 6,
                          padding: "2px 8px",
                          marginRight: 4,
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ color: "#888", fontSize: 14, marginTop: 6 }}>
                  Published: {post.date}
                </div>
              </div>
              <div
                style={{
                  textAlign: "right",
                  minWidth: 120,
                  marginLeft: "auto",
                }}
              >
                <div style={{ fontSize: 15, color: "#222" }}>
                  Views: <b>{post.views?.toLocaleString?.() || post.views}</b>
                </div>
                <div style={{ fontSize: 15, color: "#e57373", marginTop: 2 }}>
                  ❤ {post.likes?.toLocaleString?.() || post.likes} &nbsp; 💬{" "}
                  {post.comments?.toLocaleString?.() || post.comments}
                </div>

                {/* Provide Feedback button moved below likes and comments */}
                {
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      marginTop: 8,
                    }}
                  >
                    <button
                      className="feedback-button-inline"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Provide feedback clicked for post:', post.id);
                        onProvideFeedback(post.id);
                      }}
                      disabled={!post.id}
                      title={post.id ? `Provide feedback for post ${post.id}` : 'No valid post ID available'}
                      style={{
                        padding: "6px 12px",
                        border: "none",
                        borderRadius: 6,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        cursor: post.id ? "pointer" : "not-allowed",
                        transition: "all 0.3s ease",
                        background: post.id
                          ? "linear-gradient(135deg, #bee3f8 0%, #90cdf4 100%)"
                          : "linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)",
                        color: post.id ? "#1c40f5" : "#a0aec0",
                        opacity: post.id ? 1 : 0.6,
                      }}
                      onMouseEnter={(e) => {
                        if (post.id) {
                          e.target.style.background =
                            "linear-gradient(135deg, #90cdf4 0%, #63b3ed 100%)";
                          e.target.style.transform = "translateY(-1px)";
                          e.target.style.boxShadow =
                            "0 2px 8px rgba(28, 64, 245, 0.3)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (post.id) {
                          e.target.style.background =
                            "linear-gradient(135deg, #bee3f8 0%, #90cdf4 100%)";
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }
                      }}
                    >
                      Provide Feedback
                    </button>

                    {/* Complete Feedback button - only show if feedback exists */}
                    {post.feedback && (
                      <button
                        className="complete-feedback-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCompleteFeedback(post.id);
                        }}
                        style={{
                          padding: "6px 12px",
                          border: "none",
                          borderRadius: 6,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          background:
                            "linear-gradient(135deg, #c6f6d5 0%, #9ae6b4 100%)",
                          color: "#38a169",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background =
                            "linear-gradient(135deg, #9ae6b4 0%, #68d391 100%)";
                          e.target.style.transform = "translateY(-1px)";
                          e.target.style.boxShadow =
                            "0 2px 8px rgba(56, 161, 105, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background =
                            "linear-gradient(135deg, #c6f6d5 0%, #9ae6b4 100%)";
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        Withdraw/Complete Feedback
                      </button>
                    )}
                  </div>
                }
              </div>

              {/* Full width section for feedback and progress bar */}
              <div style={{ width: "100%", marginTop: 12 }}>
                {/* Feedback display - only show if feedback exists */}
                {post.feedback && (
                  <div
                    style={{
                      margin: "0 0 16px 0",
                      padding: "12px",
                      background:
                        "linear-gradient(135deg, #fbb6ce 0%, #f093fb 100%)",
                      color: "#d53f8c",
                      borderRadius: 8,
                      fontSize: 14,
                      border: "1px solid rgba(213, 63, 140, 0.2)",
                    }}
                  >
                    <strong>Feedback:</strong> {post.feedback}
                  </div>
                )}

                {/* Progress bar - only show if progressStage exists */}
                {post.progressStage !== null &&
                  post.progressStage !== undefined && (
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#2d3748",
                          marginBottom: 8,
                        }}
                      >
                        Progress:
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          width: "100%",
                          marginBottom: 8,
                        }}
                      >
                        {/* Segment 1: Feedback Provided */}
                        <div
                          style={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            background:
                              post.progressStage >= 0
                                ? "linear-gradient(90deg, #1c40f5 0%, #70a9ff 100%)"
                                : "#e2e8f0",
                            transition: "background 0.3s ease",
                          }}
                        ></div>

                        {/* Segment 2: Update Content */}
                        <div
                          style={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            background:
                              post.progressStage >= 1
                                ? "linear-gradient(90deg, #1c40f5 0%, #70a9ff 100%)"
                                : "#e2e8f0",
                            transition: "background 0.3s ease",
                          }}
                        ></div>

                        {/* Segment 3: Revision Complete */}
                        <div
                          style={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            background:
                              post.progressStage >= 2
                                ? "linear-gradient(90deg, #1c40f5 0%, #70a9ff 100%)"
                                : "#e2e8f0",
                            transition: "background 0.3s ease",
                          }}
                        ></div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          color: "#718096",
                        }}
                      >
                        <span
                          style={{
                            color:
                              post.progressStage >= 0 ? "#1c40f5" : "#718096",
                            fontWeight: post.progressStage >= 0 ? 700 : 500,
                          }}
                        >
                          Feedback Provided
                        </span>
                        <span
                          style={{
                            color:
                              post.progressStage >= 1 ? "#1c40f5" : "#718096",
                            fontWeight: post.progressStage >= 1 ? 700 : 500,
                          }}
                        >
                          Content Updated
                          <br />
                        </span>
                        <span
                          style={{
                            color:
                              post.progressStage >= 2 ? "#1c40f5" : "#718096",
                            fontWeight: post.progressStage >= 2 ? 700 : 500,
                          }}
                        >
                          Revision Complete
                        </span>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          );
        })}
    </>
  );
};

// Post management component - based on ActivePartnerships structure
const PostManagement = ({ campaignId }) => {
  const [posts, setPosts] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaignId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const axios = (await import("axios")).default;
        // 1. 获取campaign-influencer列表
        const ciRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/campaign-influencer?campaign_id=${campaignId}`,
        );
        const ciList = ciRes.data?.campaign_influencers || [];
        console.log('Campaign Influencers API Response:', ciList);
        
        // 2. 并行获取每个influencer的详细信息和post
        const influencerPromises = ciList.map(async (ci) => {
          try {
            // 并行获取influencer信息和post数据
            const [infRes, postRes] = await Promise.all([
              axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/influencer?influencer_id=${ci.influencer_id}`
              ),
              axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/post?campaign_influencer_id=${ci.campaign_influencer_id}`
              )
            ]);
            
            const inf = infRes.data?.influencer || {};
            const apiPosts = postRes.data?.posts || [];
            
            console.log('Post data for influencer:', inf.display_name, apiPosts);
            
            // 获取influencer extras（followers数）
            let followers = "N/A";
            try {
              const extrasRes = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/influencer-extras?influencer_id=${ci.influencer_id}`
              );
              followers = extrasRes.data?.extras_json?.follower_count?.toLocaleString?.() || "N/A";
            } catch (e) {
              followers = "N/A";
            }
            
            // 处理日期时区转换
            let date = ci.updated_at || "";
            if (date) {
              try {
                const utcDate = new Date(date);
                if (isNaN(utcDate.getTime())) {
                  date = "Invalid Date";
                } else {
                  date = utcDate.toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  }).replace(',', '');
                }
              } catch (e) {
                console.error('Date conversion error:', e);
                date = "Invalid Date";
              }
            }
            
            return {
              avatar_url: inf.avatar_url || "https://via.placeholder.com/64",
              name: inf.display_name || "Unknown",
              username: inf.handle || "@unknown",
              followers,
              status: "active",
              lastUpdate: date ? `Updated ${date}` : "Updated recently",
              platform: inf.platform,
              profile_url: inf.profile_url,
              posts: await Promise.all(apiPosts.map(async (p) => {
                // 处理日期时区转换
                let postDate = p.published_at || p.created_at || "";
                if (postDate) {
                  try {
                    // 直接使用 Date 构造函数解析 HTTP 日期格式
                    const localDate = new Date(postDate);
                    // 检查日期是否有效
                    if (isNaN(localDate.getTime())) {
                      console.error('Invalid date:', postDate);
                      postDate = "Invalid Date";
                    } else {
                      // 格式化为 YYYY-MM-DD 格式
                      postDate = localDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      });
                    }
                  } catch (e) {
                    console.error('Date conversion error:', e);
                    postDate = "Invalid Date";
                  }
                }
                
                // 获取反馈状态
                let feedback = null;
                let progressStage = null;
                
                if (p.post_id) {
                  try {
                    const feedbackRes = await axios.get(
                      `${process.env.REACT_APP_API_BASE_URL}/feedback?post_id=${p.post_id}`
                    );
                    const feedbackData = feedbackRes.data?.post_feedback?.[0];
                    
                    if (feedbackData) {
                      // 获取最新的评论作为反馈
                      const comments = feedbackData.comment || [];
                      if (comments.length > 0) {
                        feedback = comments[comments.length - 1];
                      }
                      
                      // 根据 feedback_status 设置进度阶段
                      const status = feedbackData.feedback_status;
                      if (status === "Feedback Provided") {
                        progressStage = 0;
                      } else if (status === "Content Updated") {
                        progressStage = 1;
                      } else if (status === "Revision Completed") {
                        // Revision Completed 等同于没有 feedback，流程已结束
                        feedback = null;
                        progressStage = null;
                      }
                      
                      if (status === "Revision Completed") {
                        console.log(`✅ Post ${p.post_id}: Feedback completed and cleared (Revision Completed)`);
                      } else {
                        console.log(`📝 Feedback Status for Post ${p.post_id}:`, {
                          status: status,
                          progressStage: progressStage,
                          feedback: feedback,
                          hasComments: comments.length > 0,
                          commentsCount: comments.length,
                          hasReply: (feedbackData.reply || []).length > 0,
                          replyCount: (feedbackData.reply || []).length
                        });
                      }
                    } else {
                      console.log(`📝 No feedback found for Post ${p.post_id}`);
                    }
                  } catch (e) {
                    console.error('Error fetching feedback for post:', p.post_id, e);
                  }
                }
                


                return {
                  id: p.post_id,
                  url: p.url,
                  title: p.title,
                  date: postDate,
                  views: p.views || 0,
                  likes: p.likes || 0,
                  comments: p.comments || 0,
                  feedback,
                  progressStage,
                };
              }))
            };
          } catch (e) {
            console.error('Error fetching influencer details:', e);
            return null;
          }
        });
        
        const influencerDetails = await Promise.all(influencerPromises);
        const validInfluencers = influencerDetails.filter(Boolean);
        setPosts(validInfluencers);
      } catch (e) {
        console.error("Failed to load partnerships data:", e);
        setError("Failed to load partnerships data");
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [campaignId]);

  // 定期检查反馈状态更新
  const checkFeedbackStatus = async () => {
    if (!posts || posts.length === 0) return;
    
    try {
      const axios = (await import("axios")).default;
      
      // 检查所有有反馈的帖子的状态
      const updatedPosts = await Promise.all(
        posts.map(async (partner) => ({
          ...partner,
          posts: await Promise.all(
            partner.posts.map(async (post) => {
              if (post.feedback && post.progressStage !== null && post.progressStage < 2) {
                try {
                  const feedbackRes = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/feedback?post_id=${post.id}`
                  );
                  const feedbackData = feedbackRes.data?.post_feedback?.[0];
                  
                  if (feedbackData) {
                    const status = feedbackData.feedback_status;
                    let newProgressStage = post.progressStage;
                    
                    let updatedPost = { ...post };
                    
                    if (status === "Content Updated" && post.progressStage < 1) {
                      updatedPost.progressStage = 1;
                      console.log(`🔄 Post ${post.id} progress updated: Stage ${post.progressStage} → Stage 1 (Content Updated)`);
                    } else if (status === "Revision Completed") {
                      // Revision Completed 表示流程结束，清除所有反馈相关状态
                      updatedPost.feedback = null;
                      updatedPost.progressStage = null;
                      console.log(`✅ Post ${post.id} feedback completed and cleared (Revision Completed)`);
                    }
                    
                    // 打印当前状态检查结果
                    console.log(`🔍 Status Check for Post ${post.id}:`, {
                      currentStage: post.progressStage,
                      newStage: updatedPost.progressStage,
                      currentFeedback: post.feedback ? 'Has feedback' : 'No feedback',
                      newFeedback: updatedPost.feedback ? 'Has feedback' : 'No feedback',
                      backendStatus: status,
                      hasReply: (feedbackData.reply || []).length > 0,
                      replyCount: (feedbackData.reply || []).length,
                      stageChanged: updatedPost.progressStage !== post.progressStage,
                      feedbackChanged: updatedPost.feedback !== post.feedback
                    });
                    
                    return updatedPost;
                  }
                } catch (e) {
                  console.error('Error checking feedback status for post:', post.id, e);
                }
              }
              return post;
            })
          )
        }))
      );
      
      setPosts(updatedPosts);
    } catch (e) {
      console.error('Error checking feedback status:', e);
    }
  };

  // 每30秒检查一次状态更新
  useEffect(() => {
    const interval = setInterval(checkFeedbackStatus, 30000);
    return () => clearInterval(interval);
  }, [posts]);

  const handleProvideFeedback = (postId) => {
    if (!postId) {
      console.error('Invalid post ID for feedback');
      alert('Cannot provide feedback: Invalid post ID');
      return;
    }
    setCurrentPostId(postId);
    setShowFeedbackModal(true);
  };

  const handleCompleteFeedback = async (postId) => {
    if (!postId) {
      console.error('Invalid post ID for completing feedback');
      alert('Cannot complete feedback: Invalid post ID');
      return;
    }
    
    try {
      const axios = (await import("axios")).default;
      
      // 调用 ending feedback API
      const response = await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/ending_feedback`,
        {
          post_id: postId
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Complete feedback API response:', response.data);
      
      // 更新本地状态
      setPosts((prevPosts) =>
        prevPosts.map((partner) => ({
          ...partner,
          posts: partner.posts.map((post) =>
            post.id === postId
              ? {
                  ...post,
                  feedback: null,
                  progressStage: null,
                }
              : post,
          ),
        })),
      );
    } catch (e) {
      console.error('Error completing feedback:', e);
      alert('Failed to complete feedback');
    }
  };

  const confirmProvideFeedback = async () => {
    if (!feedbackText.trim()) {
      alert('Please provide feedback text');
      return;
    }

    if (!currentPostId) {
      console.error('No post ID available for feedback');
      alert('Cannot submit feedback: Invalid post ID');
      return;
    }

    try {
      const axios = (await import("axios")).default;
      
      console.log('Submitting feedback for post ID:', currentPostId, 'Feedback:', feedbackText);
      
      // 调用 add feedback API
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/feedback`,
        {
          post_id: currentPostId,
          comment: feedbackText
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Add feedback API response:', response.data);
      
      // 更新本地状态
      setPosts((prevPosts) =>
        prevPosts.map((partner) => ({
          ...partner,
          posts: partner.posts.map((post) =>
            post.id === currentPostId
              ? {
                  ...post,
                  feedback: feedbackText,
                  progressStage: 0,
                }
              : post,
          ),
        })),
      );

      // 关闭模态框并重置状态
      setShowFeedbackModal(false);
      setFeedbackText("");
      setCurrentPostId(null);
    } catch (e) {
      console.error('Error providing feedback:', e);
      alert('Failed to provide feedback');
    }
  };

  return (
    <>
      <section className="post-partnerships">
        <header className="post-partnerships-title">Post Management</header>
        {loading ? (
          <div
            style={{
              padding: 60,
              textAlign: "center",
              color: "#1976d2",
              fontSize: 20,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px"
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #e3f2fd",
                borderTop: "4px solid #1976d2",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }}
            />
            <div>Loading Posts Data...</div>
          </div>
        ) : error ? (
          <div
            style={{
              padding: 60,
              textAlign: "center",
              color: "#e53e3e",
              fontSize: 20,
              width: "100%",
            }}
          >
            {error}
          </div>
        ) : posts.length === 0 ? (
          <div
            style={{
              padding: 60,
              textAlign: "center",
              color: "#888",
              fontSize: 20,
              width: "100%",
            }}
          >
            No active partnerships for this campaign.
          </div>
        ) : (
          <div className="post-partners-list">
            {posts.map((partner, index) => (
              <React.Fragment key={index}>
                <PostCard
                  {...partner}
                  expanded={index === expandedIndex}
                  onClick={() =>
                    setExpandedIndex(index === expandedIndex ? -1 : index)
                  }
                  onProvideFeedback={handleProvideFeedback}
                  onCompleteFeedback={handleCompleteFeedback}
                />
              </React.Fragment>
            ))}
          </div>
        )}
      </section>

      {/* Provide feedback modal */}
      <Modal
        show={showFeedbackModal}
        onHide={() => setShowFeedbackModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Provide Feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Please enter your feedback:</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Please provide specific feedback for improvement..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowFeedbackModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmProvideFeedback}
            disabled={!feedbackText.trim()}
          >
            Submit Feedback
          </Button>
        </Modal.Footer>
      </Modal>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

// Main component
const CreatorDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* 下面三个 state 成为"冗余"但保留不删，避免影响别处逻辑 */
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  
  // 添加accept按钮防抖保护状态
  const [isAccepting, setIsAccepting] = useState(false);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        // 从 session 获取 campaign_id
        const campaignId = sessionStorage.getItem('selectedCampaignId');
        if (!campaignId) {
          throw new Error('No campaign ID found');
        }

        // 获取申请列表
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/influencer-application?campaign_id=${campaignId}`,
          {
            method: 'GET',
            redirect: 'follow'
          }
        );
        
        const result = await response.json();
        console.log('API Response:', result);
        
        // 优雅地处理空数据情况
        const applications = result?.applications || [];
        
        // 获取每个申请者的详细信息
        const applicationsWithDetails = await Promise.all(
          applications.map(async (app) => {
            try {
              // 获取 influencer 信息
              const infRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/influencer?influencer_id=${app.influencer_id}`
              );
              const infData = await infRes.json();
              const influencer = infData.influencer || {};

              // 获取 influencer extras（followers数）
              let followers = "N/A";
              try {
                const extrasRes = await fetch(
                  `${process.env.REACT_APP_API_BASE_URL}/influencer-extras?influencer_id=${app.influencer_id}`
                );
                const extrasData = await extrasRes.json();
                followers = extrasData.extras_json?.follower_count?.toLocaleString?.() || "N/A";
              } catch (e) {
                console.error('Error fetching influencer extras:', e);
              }

              // 处理日期
              let applicationDate = app.applied_time;
              let daysRemaining = 3; // 默认3天
              let formattedDate = "Invalid Date";
              let autoAcceptDate = null;
              
              if (applicationDate) {
                try {
                  // 解析申请日期
                  const appliedDate = new Date(applicationDate);
                  const today = new Date();
                  
                  // 计算自动接受日期（申请日期+3天）
                  autoAcceptDate = new Date(appliedDate);
                  autoAcceptDate.setDate(appliedDate.getDate() + 3);
                  
                  // 计算今天距离自动接受日期的天数
                  const diffTime = autoAcceptDate.getTime() - today.getTime();
                  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // 使用 Math.floor 而不是 Math.ceil
                  daysRemaining = Math.max(0, diffDays);
                  
                  // 格式化申请日期显示
                  formattedDate = appliedDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  });

                  console.log('Date calculation:', {
                    appliedDate: appliedDate.toISOString(),
                    today: today.toISOString(),
                    autoAcceptDate: autoAcceptDate.toISOString(),
                    diffDays,
                    daysRemaining,
                    formattedDate
                  });
                } catch (e) {
                  console.error('Date conversion error:', e);
                }
              }

              return {
                id: app._id,
                influencer_id: app.influencer_id,
                avatar_url: influencer.avatar_url || "Unkown",
                name: influencer.display_name || "Unknown",
                username: influencer.handle || "@unknown",
                followers,
                platform: influencer.platform,
                profile_url: influencer.profile_url,
                applicationDate: formattedDate,
                applied_time: applicationDate,
                auto_accept_date: autoAcceptDate?.toISOString(),
                status: app.state,
                rejectionReason: app.reject_reason,
                daysRemaining
              };
            } catch (e) {
              console.error('Error fetching influencer details:', e);
              return null;
            }
          })
        );

        // 过滤掉获取失败的申请
        const validApplications = applicationsWithDetails.filter(app => app !== null);
        setApplications(validApplications);
      } catch (e) {
        console.error('Error fetching applications:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  // 添加定时器，每分钟更新一次剩余天数
  useEffect(() => {
    const timer = setInterval(() => {
      setApplications(prevApplications => 
        prevApplications.map(app => {
          if (app.status === "pending" && app.auto_accept_date) {
            try {
              const autoAcceptDate = new Date(app.auto_accept_date);
              const today = new Date();
              const diffTime = autoAcceptDate.getTime() - today.getTime();
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              const daysRemaining = Math.max(0, diffDays);
              
              // 如果剩余天数为0，自动接受申请
              if (daysRemaining === 0) {
                handleAcceptApplication(app.id);
                return { ...app, status: "accepted", daysRemaining: 0 };
              }
              
              return { ...app, daysRemaining };
            } catch (e) {
              console.error('Error updating days remaining:', e);
              return app;
            }
          }
          return app;
        })
      );
    }, 60000); // 每分钟更新一次

    return () => clearInterval(timer);
  }, []);

  const handleAcceptApplication = async (applicationId) => {
    // 防止重复点击
    if (isAccepting) {
      return;
    }
    
    setIsAccepting(true);
    
    try {
      const campaignId = sessionStorage.getItem('selectedCampaignId');
      if (!campaignId) {
        throw new Error('No campaign ID found');
      }

      // 获取当前申请信息以获取 influencer_id
      const application = applications.find(app => app.id === applicationId);
      if (!application) {
        throw new Error('Application not found');
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/accept-application`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            campaign_id: campaignId,
            influencer_id: application.influencer_id
          }),
          redirect: 'follow'
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to accept application');
      }

      // 创建campaign-influencer记录
      const campaignInfluencerResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/campaign-influencer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            campaign_id: campaignId,
            influencer_id: application.influencer_id,
            status: "approved",
            assigned_views: 30000,
            payout_cents: 500,
            payout_status: "pending"
          }),
          redirect: 'follow'
        }
      );

      if (!campaignInfluencerResponse.ok) {
        console.error('Failed to create campaign-influencer record');
        // 不抛出错误，因为accept application已经成功
      }

      // 重新获取申请列表以更新状态
      const applicationsResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/influencer-application?campaign_id=${campaignId}`,
        {
          method: 'GET',
          redirect: 'follow'
        }
      );
      const result = await applicationsResponse.json();
      
      if (!result.applications) {
        throw new Error('Invalid response format');
      }

      // 获取每个申请者的详细信息
      const applicationsWithDetails = await Promise.all(
        result.applications.map(async (app) => {
          try {
            // 获取 influencer 信息
            const infRes = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/influencer?influencer_id=${app.influencer_id}`
            );
            const infData = await infRes.json();
            const influencer = infData.influencer || {};

            // 获取 influencer extras（followers数）
            let followers = "N/A";
            try {
              const extrasRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/influencer-extras?influencer_id=${app.influencer_id}`
              );
              const extrasData = await extrasRes.json();
              followers = extrasData.extras_json?.follower_count?.toLocaleString?.() || "N/A";
            } catch (e) {
              console.error('Error fetching influencer extras:', e);
            }

            // 处理日期
            let applicationDate = app.applied_time;
            let daysRemaining = 3; // 默认3天
            let formattedDate = "Invalid Date";
            let autoAcceptDate = null;
            
            if (applicationDate) {
              try {
                // 解析申请日期
                const appliedDate = new Date(applicationDate);
                const today = new Date();
                
                // 计算自动接受日期（申请日期+3天）
                autoAcceptDate = new Date(appliedDate);
                autoAcceptDate.setDate(appliedDate.getDate() + 3);
                
                // 计算今天距离自动接受日期的天数
                const diffTime = autoAcceptDate.getTime() - today.getTime();
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                daysRemaining = Math.max(0, diffDays);
                
                // 格式化申请日期显示
                formattedDate = appliedDate.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                });
              } catch (e) {
                console.error('Date conversion error:', e);
              }
            }

            return {
              id: app._id,
              influencer_id: app.influencer_id,
              avatar_url: influencer.avatar_url || "Unkown",
              name: influencer.display_name || "Unknown",
              username: influencer.handle || "@unknown",
              followers,
              platform: influencer.platform,
              profile_url: influencer.profile_url,
              applicationDate: formattedDate,
              applied_time: applicationDate,
              auto_accept_date: autoAcceptDate?.toISOString(),
              status: app.state,
              rejectionReason: app.reject_reason,
              daysRemaining
            };
          } catch (e) {
            console.error('Error fetching influencer details:', e);
            return null;
          }
        })
      );

      // 过滤掉获取失败的申请
      const validApplications = applicationsWithDetails.filter(app => app !== null);
      setApplications(validApplications);
    } catch (e) {
      console.error('Error accepting application:', e);
      alert('Failed to accept application');
    } finally {
      setIsAccepting(false);
    }
  };

  const confirmRejectApplication = async (applicationId, reason) => {
    if (!applicationId || !reason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }

    try {
      const campaignId = sessionStorage.getItem("selectedCampaignId");
      if (!campaignId) throw new Error("No campaign ID found");

      // 找到对应申请，拿到 influencer_id
      const application = applications.find((app) => app.id === applicationId);
      if (!application) throw new Error("Application not found");

      const requestBody = {
        campaign_id: campaignId,
        influencer_id: application.influencer_id,
        reject_reason: reason,
      };

      console.log("Rejecting application:", requestBody);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reject-application`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );

      const resultText = await response.text();
      console.log("Reject-API resp:", response.status, resultText);
      if (!response.ok) throw new Error(resultText || "Reject failed");

      /* 重新拉一次列表（逻辑与接受申请时一致，代码不变） */
      const listRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/influencer-application?campaign_id=${campaignId}`
      );
      const listJson = await listRes.json();

      const applicationsWithDetails = await Promise.all(
        listJson.applications.map(async (app) => {
          try {
            // 获取influencer信息
            const infRes = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/influencer?influencer_id=${app.influencer_id}`
            );
            const infData = await infRes.json();
            const influencer = infData.influencer || {};

            // 获取followers信息
            let followers = "N/A";
            try {
              const extrasRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/influencer-extras?influencer_id=${app.influencer_id}`
              );
              const extrasData = await extrasRes.json();
              followers = extrasData.extras_json?.follower_count?.toLocaleString?.() || "N/A";
            } catch (e) {
              console.error('Error fetching influencer extras:', e);
            }

            // 处理日期
            let applicationDate = app.applied_time;
            let daysRemaining = 3;
            let formattedDate = "Invalid Date";
            let autoAcceptDate = null;
            
            if (applicationDate) {
              try {
                const appliedDate = new Date(applicationDate);
                const today = new Date();
                
                autoAcceptDate = new Date(appliedDate);
                autoAcceptDate.setDate(appliedDate.getDate() + 3);
                
                const diffTime = autoAcceptDate.getTime() - today.getTime();
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                daysRemaining = Math.max(0, diffDays);
                
                formattedDate = appliedDate.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                });
              } catch (e) {
                console.error('Date conversion error:', e);
              }
            }

            return {
              id: app._id,
              influencer_id: app.influencer_id,
              avatar_url: influencer.avatar_url || "https://via.placeholder.com/64",
              name: influencer.display_name || "Unknown",
              username: influencer.handle || "@unknown",
              followers,
              platform: influencer.platform,
              profile_url: influencer.profile_url,
              applicationDate: formattedDate,
              applied_time: applicationDate,
              auto_accept_date: autoAcceptDate?.toISOString(),
              status: app.state,
              rejectionReason: app.reject_reason,
              daysRemaining
            };
          } catch (e) {
            console.error('Error fetching influencer details:', e);
            return null;
          }
        })
      );

      setApplications(applicationsWithDetails.filter(Boolean));

      /* 清理本地 state（可保留以免未来其他逻辑使用） */
      setShowRejectModal(false);
      setSelectedApplicationId(null);
      setRejectionReason("");
    } catch (err) {
      console.error("Error rejecting application:", err);
      alert("Failed to reject application");
    }
  };

  const handleViewPost = (postUrl) => {
    window.open(postUrl, '_blank');
  };

  const handleViewInfluencer = (profileUrl) => {
    if (profileUrl) {
      window.open(profileUrl, '_blank');
    }
  };

  // Partner application card component
  const PartnerApplicationCard = ({ application, onAccept, onReject }) => {
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);
    const [error, setError] = useState(null);

    const getStatusBadge = () => {
      switch (application.status) {
        case "pending":
          return <span className="status-badge pending">Pending</span>;
        case "accepted":
          return <span className="status-badge accepted">Accepted</span>;
        case "rejected":
          return <span className="status-badge rejected">Rejected</span>;
        default:
          return <span className="status-badge pending">Pending</span>;
      }
    };

    const getDaysRemainingText = () => {
      if (application.status !== "pending") return null;
      if (application.daysRemaining <= 0) {
        return <div className="auto-accept-notice">Auto-accept soon</div>;
      }
      return (
        <div className="auto-accept-notice">
          Auto-accept in {application.daysRemaining} day
          {application.daysRemaining > 1 ? "s" : ""}
        </div>
      );
    };

    const handleRejectClick = () => {
      setShowRejectModal(true);
      setError(null);
    };

    const handleConfirmReject = async () => {
      if (!rejectionReason.trim()) {
        setError('请提供拒绝原因');
        return;
      }

      setIsRejecting(true);
      setError(null);

      try {
        await onReject(application.id, rejectionReason);
        setShowRejectModal(false);
        setRejectionReason('');
      } catch (err) {
        setError(err.message || '拒绝申请失败');
      } finally {
        setIsRejecting(false);
      }
    };

    return (
      <div className="partner-application-card">
        <div className="partner-profile">
          <div className="avatar-container">
            <img
              src={application.avatar_url}
              alt={application.name}
              className="partner-avatar"
            />
            {application.platform && platformIcons[application.platform] && (
              <img
                src={platformIcons[application.platform]}
                alt={application.platform}
                className="platform-icon"
              />
            )}
          </div>
          <div className="partner-info">
            <div className="partner-name-container">
              <h3 className="partner-name">{application.name}</h3>
              {application.profile_url && (
                <button 
                  className="influencer-link-button"
                  onClick={() => handleViewInfluencer(application.profile_url)}
                  title={`View ${application.name}'s ${application.platform} profile`}
                >
                  <FaExternalLinkAlt />
                </button>
              )}
            </div>
            <p className="partner-meta">
              {application.username} | {application.followers} followers
            </p>
            <p className="application-date">
              Applied: {application.applicationDate}
            </p>
            {getDaysRemainingText()}
            {application.status === "rejected" && application.rejectionReason && (
              <div className="rejection-reason">
                Rejection reason: {application.rejectionReason}
              </div>
            )}
          </div>
        </div>
        <div className="partner-actions">
          {getStatusBadge()}
          {application.status === "pending" && (
            <div className="action-buttons">
              <button 
                className={`action-btn accept-btn ${isAccepting ? 'accepting' : ''}`}
                onClick={() => onAccept(application.id)}
                disabled={isAccepting}
              >
                {isAccepting ? (
                  <>
                    <svg 
                      width="14" 
                      height="14" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: '6px', animation: 'spin 1s linear infinite' }}
                    >
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.3"/>
                      <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"/>
                    </svg>
                    Accepting...
                  </>
                ) : (
                  'Accept'
                )}
              </button>
              <button 
                className="action-btn reject-btn"
                onClick={handleRejectClick}
              >
                Reject
              </button>
            </div>
          )}
        </div>

        <Modal 
          show={showRejectModal} 
          onHide={() => {
            setShowRejectModal(false);
            setRejectionReason('');
            setError(null);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>拒绝申请</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>拒绝原因</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="请提供拒绝原因..."
                  isInvalid={!!error}
                />
                {error && (
                  <Form.Control.Feedback type="invalid">
                    {error}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowRejectModal(false);
                setRejectionReason('');
                setError(null);
              }}
              disabled={isRejecting}
            >
              取消
            </Button>
            <Button 
              variant="danger" 
              onClick={handleConfirmReject}
              disabled={isRejecting}
            >
              {isRejecting ? '处理中...' : '确认拒绝'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading applications...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="creator-dashboard">
      <div className="dashboard-section">
        <header className="post-partnerships-title">Partner Applications</header>
        <div className="auto-accept-notice-header">
          Applications will be auto-accepted if not rejected within 3 days
        </div>
        {applications.length === 0 ? (
          <div className="empty-state-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center',
            background: '#f8fafd',
            borderRadius: '12px',
            margin: '20px 0'
          }}>
            <div style={{
              fontSize: '48px',
              color: '#cbd5e0',
              marginBottom: '16px'
            }}>
              📝
            </div>
            <h3 style={{
              fontSize: '20px',
              color: '#2d3748',
              marginBottom: '8px',
              fontWeight: '600'
            }}>
              No Applications
            </h3>
            <p style={{
              fontSize: '16px',
              color: '#718096',
              maxWidth: '400px',
              lineHeight: '1.5'
            }}>
              There are no creator applications at the moment. New applications will appear here when they are submitted.
            </p>
          </div>
        ) : (
          <div className="applications-list">
            {applications.map((application) => (
              <PartnerApplicationCard
                key={application.id}
                application={application}
                onAccept={handleAcceptApplication}
                onReject={confirmRejectApplication}
              />
            ))}
          </div>
        )}
      </div>

      {/* Post management module - based on ActivePartnerships */}
      <PostManagement campaignId={sessionStorage.getItem('selectedCampaignId')} />
    </div>
  );
};

export default CreatorDashboard;
